import createStep1Content from "./elements/create-step-1-content";
import createCard from "./elements/create-card";
import { customEvent, generateLeadEvent } from "../../modules/tracking/AnalyticsEvents";
import { getFromStorage } from "../../used-vue/api/Storage";
require('../../modules/polyfills/remove');

export default class {
    constructor() {
        this.liveChatOpen = false;
        this.card = false;
        this.cardContent = false;
        this.chatRequested = false;
        this.startingChat = false;

        this.initFooterAvoidance();

        window.addEventListener('cookies:accepted:functional', () => {
            this.loadAgentLauncherScript();
            if (this.chatRequested) {
                this.openLiveChat();
            }
        });
        this.loadAgentLauncherScript();

        const openFromHash = () => {
            if (location.hash === '#live-chat') {
                this.openLiveChat();
            }
        };
        window.addEventListener('hashchange', openFromHash);
        openFromHash();
    }

    hasAcceptedFunctionalCookies() {
        return getFromStorage('cookies_accepted') == 1 && getFromStorage('cookies_functional') == 1;
    }

    canUseChat() {
        return !(typeof liveChatDisabled !== 'undefined' && liveChatDisabled === true)
            && this.hasAcceptedFunctionalCookies();
    }

    loadAgentLauncherScript() {
        if (!this.canUseChat()) {
            return Promise.resolve(null);
        }

        if (window.agentLauncherReady) {
            return window.agentLauncherReady;
        }

        window.agentLauncherReady = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.id = 'agent-launcher-script';
            script.src = 'https://app.agent.autoconverse.ai/dist/js/launcher.min.js';
            script.async = true;

            const timeout = window.setTimeout(() => fail(), 15000);
            const fail = () => {
                window.clearTimeout(timeout);
                script.onload = null;
                script.onerror = null;
                script.remove();
                reject(new Error('Unable to load live chat'));
            };
            script.onerror = fail;
            script.onload = async () => {
                try {
                    const launcher = window.AgentLauncher;
                    if (!launcher || typeof launcher.init !== 'function' || typeof launcher.open !== 'function') {
                        fail();
                        return;
                    }
                    await launcher.init();
                    window.clearTimeout(timeout);
                    resolve(launcher);
                } catch (error) {
                    fail();
                }
            };
            document.head.appendChild(script);
        }).catch(error => {
            window.agentLauncherReady = null;
            console.error(error);
            return null;
        });

        return window.agentLauncherReady;
    }

    /**
     * Legacy page entry points still call these setters. AutoConverse needs no context.
     */
    setVehicleData() {}

    setCustomerData() {}

    setCustomerType() {}

    setCustomerPostcode() {}

    /**
     * Create the basic card HTML
     */
    createCard() {
        if (this.card !== false) {
            return;
        }
        const card = createCard(this);
        this.card = card.card;
        this.cardContent = card.content;
    }

    /**
     * Remove the card HTML
     */
    removeCard() {
        if (this.card === false) {
            return;
        }
        this.card.parentNode.remove();
        this.card = false;
        this.cardContent = false;
        this.liveChatOpen = false;
        this.chatRequested = false;
        // Remove the #live-chat hash when removing the card
        const hash = location.hash.substring(1);
        if (hash == 'live-chat') {
            location.hash = '';
        }
    }

    /**
     * Render the chat button, or defer it until functional cookies are accepted.
     */
    openLiveChat() {
        this.chatRequested = true;
        if (!this.canUseChat() || this.liveChatOpen) {
            return;
        }

        this.loadAgentLauncherScript();
        this.liveChatOpen = true;
        this.createCard();
        this.card.classList.add('live-chat-card__step-1');
        this.cardContent.appendChild(createStep1Content(() => {
            this.startChat();
            customEvent('Live Chat', 'Start Chat - Card', 'Clicked');
        }));
    }

    initLiveChat() {
        return this.openLiveChat();
    }

    async openAgentLauncher() {
        const launcher = await this.loadAgentLauncherScript();
        if (!launcher || !this.canUseChat()) {
            return false;
        }
        await launcher.open();
        return true;
    }

    /**
     * Page buttons may launch the provider directly without a card.
     */
    async startChat() {
        if (!this.canUseChat() || this.startingChat) {
            return;
        }

        this.startingChat = true;
        if (this.card) {
            this.card.classList.add('live-chat-card--loading');
        }
        try {
            if (await this.openAgentLauncher()) {
                customEvent('Live Chat', 'Start Chat', 'AgentLauncher widget opened');
                generateLeadEvent('live_chat');
            }
        } catch (error) {
            console.error('Unable to open live chat', error);
        } finally {
            this.startingChat = false;
            if (this.card) {
                this.card.classList.remove('live-chat-card--loading');
            }
        }
    }

    initFooterAvoidance() {
        const footer = document.querySelector(".footer-social__outer-container");
        if (!footer) {
            return;
        }

        const observer = new IntersectionObserver(([entry]) => {
            if (!this.card) {
                return;
            }

            if (entry.isIntersecting) {
                const footerHeight = footer.offsetHeight;
                this.card.style.transform = `translateY(-${footerHeight}px)`;
            } else {
                this.card.style.transform = "translateY(0)";
            }
        });

        observer.observe(footer);
    }
}
